import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaEllipsisV } from 'react-icons/fa';
import styles from '../../styles/partial/ActionMenuButton.module.css';

const ActionMenuButton = ({ 
  itemId, 
  menuItems = [], 
  containerClassName = '',
  onMenuStateChange = null,
  isOpen = false,
  onToggle = null,
  triggerLabel = undefined
}) => {
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  // Use external state if provided, otherwise use internal state
  const isMenuOpen = onToggle ? isOpen : (menuOpen === itemId);
  const shouldShowMenu = isMenuOpen;

  // Calculate menu position dynamically when menu should be shown
  const calculateMenuPosition = () => {
    if (!shouldShowMenu) return { x: 0, y: 0 };
    
    // Find the button element for this specific itemId
    const buttonContainers = document.querySelectorAll('[data-action-menu-container]');
    let targetButton = null;
    
    buttonContainers.forEach(container => {
      const button = container.querySelector('button');
      if (button && button.getAttribute('data-item-id') === String(itemId)) {
        targetButton = button;
      }
    });
    
    if (targetButton) {
      const buttonRect = targetButton.getBoundingClientRect();
      const menuWidth = 120;
      
      let x = buttonRect.right - menuWidth;
      let y = buttonRect.bottom + 2;
      
      if (x < 0) {
        x = buttonRect.left;
      }
      if (x + menuWidth > window.innerWidth) {
        x = window.innerWidth - menuWidth - 10;
      }
      
      return { x, y };
    }
    
    return menuPosition; // Fallback to stored position
  };

  const handleMenuToggle = (eventId, event) => {
    if (onToggle) {
      // Use external state management
      if (isOpen) {
        onToggle(null);
      } else {
        // Calculate position when opening menu
        const buttonRect = event.currentTarget.getBoundingClientRect();
        const menuWidth = 120;
        
        // Calculate optimal position
        let x = buttonRect.right - menuWidth;
        let y = buttonRect.bottom + 2;
        
        // Adjust if menu would go off-screen
        if (x < 0) {
          x = buttonRect.left;
        }
        if (x + menuWidth > window.innerWidth) {
          x = window.innerWidth - menuWidth - 10;
        }
        
        setMenuPosition({ x, y });
        onToggle(eventId);
      }
    } else {
      // Fallback to internal state management
      if (menuOpen === eventId) {
        setMenuOpen(null);
        onMenuStateChange && onMenuStateChange(null);
      } else {
        // Calculate position when opening menu
        const buttonRect = event.currentTarget.getBoundingClientRect();
        const menuWidth = 120;
        
        let x = buttonRect.right - menuWidth;
        let y = buttonRect.bottom + 2;
        
        if (x < 0) {
          x = buttonRect.left;
        }
        if (x + menuWidth > window.innerWidth) {
          x = window.innerWidth - menuWidth - 10;
        }
        
        setMenuPosition({ x, y });
        setMenuOpen(eventId);
        onMenuStateChange && onMenuStateChange(eventId);
      }
    }
  };

  useEffect(() => {
    if (!shouldShowMenu) return; // Only add listener when menu is open

    const handleClickOutside = (event) => {
      // Check if clicked on a menu button or inside the portal menu
      const menuContainers = document.querySelectorAll('[data-action-menu-container]');
      const portalMenu = document.querySelector('[data-action-menu-portal]');
      
      let clickedInsideMenu = false;
      
      // Check menu containers (buttons)
      menuContainers.forEach(container => {
        if (container.contains(event.target)) {
          clickedInsideMenu = true;
        }
      });
      
      // Check portal menu
      if (portalMenu && portalMenu.contains(event.target)) {
        clickedInsideMenu = true;
      }
      
      if (!clickedInsideMenu) {
        if (onToggle) {
          onToggle(null);
        } else {
          setMenuOpen(null);
          onMenuStateChange && onMenuStateChange(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shouldShowMenu, onToggle, onMenuStateChange]);

  const handleMenuItemClick = (action) => {
    action();
    if (onToggle) {
      onToggle(null);
    } else {
      setMenuOpen(null);
      onMenuStateChange && onMenuStateChange(null);
    }
  };

  return (
    <>
      <div 
        className={`${styles.contextMenuContainer} ${containerClassName}`} 
        data-action-menu-container
      >
        <button 
          className={styles.triggerBtn}
          onClick={(e) => handleMenuToggle(itemId, e)}
          data-item-id={itemId}
          aria-label={triggerLabel}
        >
          <FaEllipsisV />
        </button>
      </div>
      
      {/* Portal-based context menu */}
      {shouldShowMenu && createPortal(
        <div 
          className={styles.contextMenu}
          data-action-menu-portal
          style={{
            position: 'fixed',
            left: `${calculateMenuPosition().x}px`,
            top: `${calculateMenuPosition().y}px`,
            zIndex: 999999
          }}
        >
          {menuItems.map((item) => (
            <button 
              key={item.label}
              onClick={() => handleMenuItemClick(item.action)}
            >
              {item.icon && <item.icon className={styles.contextMenuIcon} />}
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default ActionMenuButton;
